import { useState, useEffect, useCallback } from 'react';
import { fetchProperties, fetchWorkPlanSummary, fetchPropertyPayments } from '@/lib/api';
import { formatMXN, formatMXNCompact, formatDate } from '@/lib/formatters';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Printer, ChevronLeft, Home, Calendar, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

export default function TimelineViviendas() {
  const [properties, setProperties] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProp, setSelectedProp] = useState(null);

  const load = useCallback(async () => {
    try {
      const [props, pays] = await Promise.all([fetchProperties(), fetchPropertyPayments()]);
      // Enrich with summaries
      const enriched = await Promise.all(props.map(async p => {
        try {
          const s = await fetchWorkPlanSummary(p.id);
          return { ...p, actual_total: s?.actual_total || 0, projected_total: s?.projected_total || 0, status_obra: s?.status || 'en_linea' };
        } catch { return { ...p, actual_total: 0, projected_total: 0, status_obra: 'en_linea' }; }
      }));
      setProperties(enriched);
      setPayments(pays);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-[500px] rounded-xl" /></div>;

  if (selectedProp) {
    return <PropertyTimeline prop={selectedProp} payments={payments.filter(p => p.property_id === selectedProp.id)} onBack={() => setSelectedProp(null)} />;
  }

  return <GlobalTimeline properties={properties} payments={payments} onSelect={setSelectedProp} />;
}

/* ══════════════════════════════════════════════
   GLOBAL TIMELINE
   ══════════════════════════════════════════════ */
function GlobalTimeline({ properties, payments, onSelect }) {
  // Calculate date range
  const today = new Date();
  const dates = properties.flatMap(p => [p.deed_date, p.delivery_date].filter(Boolean)).map(d => new Date(d));
  const minDate = new Date(Math.min(today.getTime() - 90 * 86400000, ...dates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(today.getTime() + 180 * 86400000, ...dates.map(d => d.getTime())));
  const totalDays = (maxDate - minDate) / 86400000;

  const toPercent = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return Math.max(0, Math.min(100, ((d - minDate) / 86400000) / totalDays * 100));
  };
  const todayPct = toPercent(today.toISOString().substring(0, 10));

  // Generate month labels
  const months = [];
  const cur = new Date(minDate);
  cur.setDate(1);
  while (cur <= maxDate) {
    const pct = toPercent(cur.toISOString().substring(0, 10));
    months.push({ label: cur.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' }), pct });
    cur.setMonth(cur.getMonth() + 1);
  }

  return (
    <div className="space-y-6 print-container">
      <div className="flex items-center justify-between print-hide">
        <PageHeader title="Timeline de Viviendas" subtitle={`${properties.length} viviendas`} />
        <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="h-3.5 w-3.5 mr-1.5" /> Imprimir</Button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#F05A28]" /> Hoy</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-500" /> Avance</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-slate-200" /> Restante</span>
        <span className="flex items-center gap-1">⬥ Escrituración</span>
        <span className="flex items-center gap-1">★ Entrega</span>
      </div>

      <Card className="rounded-xl border bg-white overflow-hidden">
        <CardContent className="p-0">
          {/* Month headers */}
          <div className="relative h-8 border-b bg-slate-50">
            {months.map((m, i) => (
              <div key={i} className="absolute text-[9px] text-slate-400 uppercase font-semibold" style={{ left: `${m.pct}%`, top: '8px' }}>{m.label}</div>
            ))}
            {/* Today line */}
            <div className="absolute top-0 bottom-0 w-px bg-[#F05A28] z-10" style={{ left: `${todayPct}%` }}>
              <span className="absolute -top-0 -translate-x-1/2 text-[8px] font-bold text-[#F05A28] bg-white px-1">HOY</span>
            </div>
          </div>

          {/* Property rows */}
          {properties.map(p => {
            const deedPct = toPercent(p.deed_date);
            const deliveryPct = toPercent(p.delivery_date);
            const avance = p.actual_total || 0;
            const targetPct = deliveryPct || deedPct || 50;
            const startPct = Math.max(0, targetPct - 40);
            const barWidth = targetPct - startPct;
            const fillWidth = barWidth * (avance / 100);
            const propPayments = payments.filter(pp => pp.property_id === p.id && pp.payment_status !== 'cancelled');
            const daysToDelivery = p.delivery_date ? Math.round((new Date(p.delivery_date) - today) / 86400000) : p.deed_date ? Math.round((new Date(p.deed_date) - today) / 86400000) : null;

            return (
              <div key={p.id} className="relative flex items-center border-b hover:bg-slate-50/50 cursor-pointer group" style={{ height: '48px' }} onClick={() => onSelect(p)}>
                {/* Label */}
                <div className="w-[180px] shrink-0 px-3 z-10 bg-white group-hover:bg-slate-50/50">
                  <p className="text-[11px] font-semibold text-slate-800 truncate">Lt {p.lot} Mz {p.block}</p>
                  <p className="text-[9px] text-slate-400 truncate">{p.name || p.modelo} · {avance}%</p>
                </div>

                {/* Timeline bar area */}
                <div className="flex-1 relative h-full">
                  {/* Month grid lines */}
                  {months.map((m, i) => <div key={i} className="absolute top-0 bottom-0 border-l border-slate-100" style={{ left: `${m.pct}%` }} />)}

                  {/* Today line */}
                  <div className="absolute top-0 bottom-0 w-px bg-[#F05A28]/30 z-5" style={{ left: `${todayPct}%` }} />

                  {/* Progress bar */}
                  <div className="absolute top-[14px] h-[20px] rounded-full bg-slate-100 overflow-hidden" style={{ left: `${startPct}%`, width: `${barWidth}%` }}>
                    <div className={`h-full rounded-full transition-all ${avance >= 100 ? 'bg-emerald-500' : avance > 50 ? 'bg-emerald-400' : avance > 25 ? 'bg-amber-400' : 'bg-slate-300'}`} style={{ width: `${(avance / 100) * 100}%` }} />
                  </div>

                  {/* Avance label */}
                  <span className="absolute text-[9px] font-bold tabular-nums" style={{ left: `${startPct + fillWidth + 0.5}%`, top: '16px', color: avance >= 50 ? '#059669' : '#94a3b8' }}>{avance}%</span>

                  {/* Deed marker */}
                  {deedPct !== null && <div className="absolute top-[10px] text-[12px]" style={{ left: `${deedPct}%`, transform: 'translateX(-50%)' }} title={`Escrituración: ${formatDate(p.deed_date)}`}>⬥</div>}

                  {/* Delivery marker */}
                  {deliveryPct !== null && <div className="absolute top-[10px] text-[11px] text-[#F05A28]" style={{ left: `${deliveryPct}%`, transform: 'translateX(-50%)' }} title={`Entrega: ${formatDate(p.delivery_date)}`}>★</div>}

                  {/* Days remaining */}
                  {daysToDelivery !== null && (
                    <span className={`absolute text-[8px] font-medium ${daysToDelivery < 30 ? 'text-red-500' : daysToDelivery < 90 ? 'text-amber-500' : 'text-slate-400'}`} style={{ right: '8px', top: '18px' }}>
                      {daysToDelivery > 0 ? `${daysToDelivery}d` : daysToDelivery === 0 ? 'HOY' : `${Math.abs(daysToDelivery)}d atraso`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Print styles */}
      <style>{`
        @media print {
          .print-hide { display: none !important; }
          .print-container { padding: 0; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}


/* ══════════════════════════════════════════════
   PROPERTY TIMELINE (DETAIL)
   ══════════════════════════════════════════════ */
function PropertyTimeline({ prop, payments, onBack }) {
  const today = new Date();
  const avance = prop.actual_total || 0;
  const daysToDelivery = prop.delivery_date ? Math.round((new Date(prop.delivery_date) - today) / 86400000) : null;
  const daysToDeed = prop.deed_date ? Math.round((new Date(prop.deed_date) - today) / 86400000) : null;

  // Sort payments by scheduled_date
  const sortedPays = [...payments].sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || ''));

  // Timeline range
  const allDates = [prop.deed_date, prop.delivery_date, ...sortedPays.map(p => p.scheduled_date), ...sortedPays.map(p => p.actual_date)].filter(Boolean);
  const minDate = new Date(Math.min(...allDates.map(d => new Date(d).getTime()), today.getTime() - 60 * 86400000));
  const maxDate = new Date(Math.max(...allDates.map(d => new Date(d).getTime()), today.getTime() + 90 * 86400000));
  const totalDays = (maxDate - minDate) / 86400000;
  const toPercent = (dateStr) => {
    if (!dateStr) return null;
    return Math.max(0, Math.min(100, ((new Date(dateStr) - minDate) / 86400000) / totalDays * 100));
  };
  const todayPct = toPercent(today.toISOString().substring(0, 10));

  const months = [];
  const cur = new Date(minDate); cur.setDate(1);
  while (cur <= maxDate) {
    months.push({ label: cur.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' }), pct: toPercent(cur.toISOString().substring(0, 10)) });
    cur.setMonth(cur.getMonth() + 1);
  }

  return (
    <div className="space-y-6 print-container">
      <div className="flex items-center gap-3 print-hide">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}><ChevronLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h1 className="text-[22px] font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Lt {prop.lot} Mz {prop.block}</h1>
          <p className="text-[13px] text-slate-400">{prop.name || prop.modelo}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="h-3.5 w-3.5 mr-1.5" /> Imprimir</Button>
      </div>

      {/* Print header */}
      <div className="hidden print-show">
        <h1 className="text-xl font-bold">Lt {prop.lot} Mz {prop.block} — {prop.name || prop.modelo}</h1>
        <p className="text-sm text-slate-500">constructOS — Reporte de vivienda</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="rounded-xl border"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-slate-400">Avance Obra</p>
          <p className={`text-[20px] font-bold tabular-nums ${avance >= 100 ? 'text-emerald-600' : 'text-[#F05A28]'}`} style={{ fontFamily: 'Space Grotesk' }}>{avance}%</p>
        </CardContent></Card>
        <Card className="rounded-xl border"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-slate-400">Escrituración</p>
          <p className="text-[14px] font-bold tabular-nums">{prop.deed_date ? formatDate(prop.deed_date) : '—'}</p>
          {daysToDeed !== null && <p className={`text-[10px] ${daysToDeed < 30 ? 'text-red-500' : 'text-slate-400'}`}>{daysToDeed > 0 ? `en ${daysToDeed} días` : daysToDeed === 0 ? 'HOY' : `${Math.abs(daysToDeed)}d atraso`}</p>}
        </CardContent></Card>
        <Card className="rounded-xl border"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-slate-400">Entrega</p>
          <p className="text-[14px] font-bold tabular-nums">{prop.delivery_date ? formatDate(prop.delivery_date) : '—'}</p>
          {daysToDelivery !== null && <p className={`text-[10px] ${daysToDelivery < 30 ? 'text-red-500' : 'text-slate-400'}`}>{daysToDelivery > 0 ? `en ${daysToDelivery} días` : 'Entregada'}</p>}
        </CardContent></Card>
        <Card className="rounded-xl border"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-slate-400">Precio Venta</p>
          <p className="text-[14px] font-bold tabular-nums">{formatMXNCompact(prop.sale_price)}</p>
        </CardContent></Card>
      </div>

      {/* Timeline */}
      <Card className="rounded-xl border bg-white overflow-hidden">
        <CardContent className="p-4">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-4">Timeline</p>
          <div className="relative" style={{ height: '120px' }}>
            {/* Month grid */}
            {months.map((m, i) => (
              <div key={i} className="absolute top-0 bottom-0 border-l border-slate-100" style={{ left: `${m.pct}%` }}>
                <span className="text-[8px] text-slate-400 ml-1">{m.label}</span>
              </div>
            ))}
            {/* Today */}
            <div className="absolute top-0 bottom-0 w-px bg-[#F05A28] z-10" style={{ left: `${todayPct}%` }}>
              <span className="absolute top-0 -translate-x-1/2 text-[7px] font-bold text-[#F05A28] bg-white px-0.5">HOY</span>
            </div>
            {/* Deed marker */}
            {prop.deed_date && <div className="absolute text-[16px]" style={{ left: `${toPercent(prop.deed_date)}%`, top: '25px', transform: 'translateX(-50%)' }} title="Escrituración">⬥</div>}
            {/* Delivery marker */}
            {prop.delivery_date && <div className="absolute text-[16px] text-[#F05A28]" style={{ left: `${toPercent(prop.delivery_date)}%`, top: '25px', transform: 'translateX(-50%)' }} title="Entrega">★</div>}
            {/* Payment markers */}
            {sortedPays.map((pp, i) => {
              const pct = toPercent(pp.scheduled_date);
              if (pct === null) return null;
              const isPaid = pp.payment_status === 'received';
              return (
                <div key={i} className="absolute" style={{ left: `${pct}%`, top: '55px', transform: 'translateX(-50%)' }}>
                  <div className={`h-3 w-3 rounded-full border-2 ${isPaid ? 'bg-emerald-500 border-emerald-600' : pp.payment_status === 'overdue' ? 'bg-red-500 border-red-600' : 'bg-white border-slate-300'}`} />
                  <p className="text-[7px] text-slate-400 text-center mt-0.5 whitespace-nowrap">{formatMXNCompact(pp.scheduled_amount)}</p>
                </div>
              );
            })}
            {/* Progress bar */}
            <div className="absolute h-3 rounded-full bg-slate-100 overflow-hidden" style={{ left: '5%', right: '5%', top: '90px' }}>
              <div className={`h-full rounded-full ${avance >= 100 ? 'bg-emerald-500' : 'bg-[#F05A28]'}`} style={{ width: `${avance}%` }} />
            </div>
            <span className="absolute text-[9px] font-bold text-[#F05A28]" style={{ left: `${5 + avance * 0.9}%`, top: '78px' }}>{avance}%</span>
          </div>
        </CardContent>
      </Card>

      {/* Payments table */}
      <Card className="rounded-xl border bg-white">
        <CardContent className="p-4">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">Calendario de Pagos</p>
          <div className="space-y-1.5">
            {sortedPays.map(pp => {
              const isPaid = pp.payment_status === 'received';
              const isPartial = pp.payment_status === 'partial';
              const isOverdue = pp.payment_status === 'overdue';
              return (
                <div key={pp.id} className={`flex items-center gap-3 rounded-lg px-3 py-2 text-xs ${isPaid ? 'bg-emerald-50/50' : isOverdue ? 'bg-red-50/50' : 'bg-slate-50/50'}`}>
                  <div className={`h-2 w-2 rounded-full shrink-0 ${isPaid ? 'bg-emerald-500' : isPartial ? 'bg-amber-500' : isOverdue ? 'bg-red-500' : 'bg-slate-300'}`} />
                  <span className="text-[11px] tabular-nums w-20">{formatDate(pp.scheduled_date)}</span>
                  <Badge variant="outline" className="text-[8px]">{pp.payment_type === 'anticipo' ? 'Anticipo' : pp.payment_type === 'finiquito' ? 'Finiquito' : 'Intermedio'}</Badge>
                  <span className="flex-1" />
                  <span className="tabular-nums font-semibold">{formatMXN(pp.scheduled_amount)}</span>
                  {isPaid && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                  {isOverdue && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                  {isPartial && <Clock className="h-3.5 w-3.5 text-amber-500" />}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
          .print-show { display: block !important; }
          .print-container { padding: 0; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .print-show { display: none; }
      `}</style>
    </div>
  );
}
